/**
 * A full-screen page with one card on it: the shape both the settings and the
 * instructions use.
 *
 * Shared rather than copied because the delicate part is not the chrome, it is
 * the mouse. These pages are opened with the cursor, so a click inside one must
 * never reach the canvas underneath and re-grab pointer lock - and a click
 * outside the card means "I am done here", which is what the rest of the web
 * has taught everyone a dimmed backdrop does.
 *
 * The card is one button deep, so its `MenuButtons` is a list of one - but it
 * is the same list every other menu has, and that is what puts Enter on the
 * close button whether or not anything on the page happens to be focused.
 */

import { MenuButtons } from './MenuButtons';
import { button, el, setHidden } from './dom';

export class MenuScreen {
  readonly root = el('div', 'overlay overlay--modal');
  /** Where the page's own content goes. */
  readonly body = el('div', 'menu-card__body');
  /** The close button, for Enter. See `Game.keyboardMenu`. */
  readonly buttons: MenuButtons;

  constructor(
    title: string,
    subtitle: string,
    onClose: () => void,
    closeLabel = 'VALMIS',
    onConfirm = onClose,
  ) {
    const card = el('div', 'menu-card');

    const head = el('div', 'menu-card__head');
    head.append(el('h2', 'menu-card__title', title), el('p', 'menu-card__sub', subtitle));

    const close = button(closeLabel, onConfirm, 'btn btn--primary');
    this.buttons = new MenuButtons([close]);

    const foot = el('div', 'menu-card__foot');
    foot.append(el('span', 'menu-card__foot-hint', 'ESC tai ENTER sulkee'), close);

    card.append(head, this.body, foot);
    this.root.appendChild(card);

    this.root.addEventListener('mousedown', (event) => {
      // Stopped for the whole page, card included: the canvas below is still
      // listening for a mousedown to lock the pointer to.
      event.stopPropagation();
      if (event.target === this.root) onClose();
    });
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  setVisible(visible: boolean): void {
    setHidden(this.root, !visible);
    this.buttons.setActive(visible);
  }
}
